import { DEFAULT_GPT_IMAGE_MODEL } from '@/constants/gptImageModels';
import {
  PaperClipIcon,
  QuestionMarkCircleIcon,
  XIcon,
} from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';
import { memo, useEffect, useRef, useState } from 'react';
import ReactTextareaAutosize from 'react-textarea-autosize';

import { SimpleTooltip } from '@/components/ui/tooltip';

import { editModels, isPremiumEditModel } from '@/constants/imageModels';
import { getNanoBananaCredits } from '@/constants/nanoBananaCredits';

import AspectRatioSelector from '../AspectRatioSelector';
import ResolutionSelector from '../ResolutionSelector';
import Toggle from '../../atoms/Toggle';
import Button from '../../buttons/Button';
import Spinner from '../../Spinner';

interface PromptFormProps {
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  initialPrompt: string;
  isGenerating?: boolean;
  isCanvasMode?: boolean;
  onModelChange?: (model: string) => void;
  hasReferenceImages?: boolean;
  resolution?: string | null;
  additionalImages?: string[];
  onAddImages?: (files: File[]) => void;
  onRemoveImage?: (index: number) => void;
  isFileLoading?: boolean;
  // Dimensions and resolution controls
  dimensions?: string;
  onDimensionsChange?: (value: string) => void;
  dimensionOptions?: string[];
  resolutionValue?: string;
  onResolutionChange?: (value: string) => void;
  resolutionOptions?: string[];
  // Visual mode
  editMode?: 'prompt' | 'visual';
  onEditModeChange?: (mode: 'prompt' | 'visual') => void;
  hasValidAnnotations?: boolean;
  isFreePlan?: boolean;
}

const PromptForm = memo(function PromptForm({
  onSubmit,
  initialPrompt,
  isGenerating = false,
  isCanvasMode = false,
  onModelChange,
  hasReferenceImages = false,
  resolution = null,
  additionalImages = [],
  onAddImages,
  onRemoveImage,
  isFileLoading = false,
  dimensions,
  onDimensionsChange,
  dimensionOptions = [],
  resolutionValue,
  onResolutionChange,
  resolutionOptions = [],
  editMode = 'prompt',
  onEditModeChange,
  hasValidAnnotations = false,
  isFreePlan = false,
}: PromptFormProps) {
  const { t } = useLocale('ai-images');
  const [prompt, setPrompt] = useState(initialPrompt);
  const [useHD, setUseHD] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Filter models based on whether reference images are present
  const availableModels = hasReferenceImages
    ? editModels.filter(
        (model) =>
          model.value === 'FLUX.2 [max]' ||
          model.value === 'FLUX.2 [pro]' ||
          model.value === 'FLUX.2 [flex]' ||
          model.value === 'Seedream 4.5' ||
          model.value === 'Seedream 5.0 Lite' ||
          model.value === 'Seedream 5.0 Pro' ||
          model.value === 'Seedream 4.0' ||
          model.value === 'Nano Banana' ||
          model.value === 'Nano Banana 2' ||
          model.value === 'Nano Banana 2.1' ||
          model.value === 'Nano Banana 2 Lite' ||
          model.value === 'Nano Banana Pro' ||
          model.value === 'OpenAI GPT Image 1' ||
          model.value === 'OpenAI GPT Image 1.5' ||
          model.value === 'OpenAI GPT Image 2 Low' ||
          model.value === 'OpenAI GPT Image 2 Medium' ||
          model.value === 'OpenAI GPT Image 2 High' ||
          model.value === 'OpenAI GPT Image 2.5 Flare Low' ||
          model.value === 'OpenAI GPT Image 2.5 Flare Medium' ||
          model.value === 'OpenAI GPT Image 2.5 Flare High' ||
          model.value === 'OpenAI GPT Image 2.5 Sunburst Medium' ||
          model.value === 'OpenAI GPT Image 2.5 Sunburst High',
      )
    : editModels;

  // Default to the platform default image model, else the first available
  // model
  const [selectedModel, setSelectedModel] = useState(
    availableModels.find((m) => m.value === DEFAULT_GPT_IMAGE_MODEL)?.value ||
      availableModels[0]?.value ||
      'Nano Banana',
  );

  // Notify parent when model changes
  useEffect(() => {
    if (onModelChange) {
      onModelChange(selectedModel);
    }
  }, [selectedModel, onModelChange]);

  // Update selected model when available models change; premium models are
  // locked on the Free plan (mirrors ai-videos model gating)
  useEffect(() => {
    if (
      !availableModels.find((m) => m.value === selectedModel) ||
      (isFreePlan && isPremiumEditModel(selectedModel))
    ) {
      // Prefer the platform default; it is not premium, so it is valid for
      // Free users too when present in the filtered list.
      const preferred = availableModels.find(
        (m) => m.value === DEFAULT_GPT_IMAGE_MODEL,
      );
      const fallback =
        preferred ||
        (isFreePlan
          ? availableModels.find((m) => !isPremiumEditModel(m.value))
          : availableModels[0]);
      setSelectedModel(fallback?.value || 'Nano Banana');
    }
  }, [availableModels, selectedModel, isFreePlan]);

  const isSubmitDisabled =
    isGenerating ||
    (editMode === 'prompt' && !prompt.trim()) ||
    (editMode === 'visual' && !hasValidAnnotations);

  const getCreditsForModel = (model: string) => {
    const modelConfig = editModels.find((m) => m.value === model);
    const baseCredits = modelConfig?.credits || 2;

    // For OpenAI GPT Image 1.5, adjust credits based on useHD
    if (model === 'OpenAI GPT Image 1.5') {
      return useHD ? 8 : 2;
    }

    // Nano Banana family — see @/constants/nanoBananaCredits.
    const nanoBananaCredits = getNanoBananaCredits(model, resolution);
    if (nanoBananaCredits !== null) {
      return nanoBananaCredits;
    }

    // For FLUX.2 [max], adjust credits based on resolution
    if (model === 'FLUX.2 [max]' && resolution === '2K') {
      return 5;
    }

    // For FLUX.2 [flex], adjust credits based on resolution
    if (model === 'FLUX.2 [flex]' && resolution === '2K') {
      return 6;
    }

    // For Seedream 5.0 Pro, adjust credits based on resolution
    if (model === 'Seedream 5.0 Pro' && resolution === '1K') {
      return 4;
    }

    // For Grok Imagine 2.0, adjust credits based on resolution
    if (model === 'Grok Imagine 2.0' && resolution === '2K') {
      return 5;
    }

    return baseCredits;
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    formData.set('prompt', prompt);
    formData.set('useHD', useHD ? '1' : '0');
    formData.set('editModel', selectedModel);
    onSubmit(e);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0 && onAddImages) {
      onAddImages(Array.from(files));
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  return (
    <form onSubmit={handleSubmit} className='animate-in fade-in duration-700'>
      <div className='flex flex-col space-y-4'>
        {/* Textarea with attachment button */}
        <div className='relative flex flex-col rounded-3xl border border-gray-200 bg-white px-3 py-1 shadow-[0_9px_9px_0px_rgba(0,0,0,0.01),_0_2px_5px_0px_rgba(0,0,0,0.06)] transition-all duration-150 ease-in-out dark:border-gray-700 dark:bg-slate-800 dark:shadow-none'>
          <ReactTextareaAutosize
            value={editMode === 'visual' ? '' : prompt}
            onChange={(e) => setPrompt(e.target.value)}
            name='prompt'
            placeholder={
              editMode === 'visual'
                ? t('Add annotations by clicking on the image')
                : t('What do you want to change?')
            }
            className='w-full resize-none border-0 bg-transparent px-1 py-3 placeholder:text-gray-400 focus:outline-none focus:ring-0 dark:text-white dark:placeholder:text-gray-500 disabled:cursor-not-allowed'
            rows={3}
            minRows={3}
            maxRows={8}
            disabled={isGenerating || editMode === 'visual'}
          />

          {/* Reference images preview */}
          {additionalImages.length > 0 && (
            <div className='flex flex-wrap gap-2 px-1 pb-2'>
              {additionalImages.map((imageUrl, index) => (
                <div
                  key={index}
                  className='relative group w-12 h-12 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600'
                >
                  <img
                    src={imageUrl}
                    alt={`Reference ${index + 1}`}
                    className='w-full h-full object-cover'
                  />
                  {onRemoveImage && (
                    <button
                      type='button'
                      onClick={() => onRemoveImage(index)}
                      className='absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center'
                    >
                      <XIcon className='w-4 h-4 text-white' />
                    </button>
                  )}
                  {imageUrl.startsWith('blob:') && (
                    <div className='absolute inset-0 bg-black/30 flex items-center justify-center'>
                      <Spinner className='w-4 h-4 text-white animate-spin' />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Bottom bar with attachment button */}
          {!isCanvasMode && onAddImages && (
            <div className='flex items-center justify-between px-1 py-2 border-t border-gray-100 dark:border-gray-700/50'>
              <div className='flex items-center gap-3'>
                {/* Prompt/Visual Toggle */}
                <div className='flex items-center rounded-full bg-gray-100 dark:bg-gray-800 p-0.5'>
                  <button
                    type='button'
                    onClick={() => onEditModeChange?.('prompt')}
                    className={`px-3 py-1 text-xs font-medium rounded-full transition-all ${
                      editMode === 'prompt'
                        ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    {t('Prompt')}
                  </button>
                  <button
                    type='button'
                    onClick={() => onEditModeChange?.('visual')}
                    className={`px-3 py-1 text-xs font-medium rounded-full transition-all ${
                      editMode === 'visual'
                        ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    {t('Visual')}
                  </button>
                </div>

                <div className='w-px h-5 bg-gray-200 dark:bg-gray-700' />

                <input
                  ref={fileInputRef}
                  type='file'
                  accept='image/jpeg,image/png,image/webp'
                  multiple
                  className='hidden'
                  onChange={handleFileSelect}
                  disabled={isGenerating || isFileLoading}
                />
                <SimpleTooltip title={t('Add reference images')} side='top'>
                  <button
                    type='button'
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isGenerating || isFileLoading}
                    className='p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-gray-300 dark:hover:bg-gray-800 transition-colors disabled:opacity-50'
                  >
                    {isFileLoading ? (
                      <Spinner className='w-5 h-5 animate-spin' />
                    ) : (
                      <PaperClipIcon className='w-5 h-5' />
                    )}
                  </button>
                </SimpleTooltip>
                {additionalImages.length > 0 && (
                  <span className='text-xs text-gray-400'>
                    {additionalImages.length} {t('image(s)')}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Options Section */}
        <div className='space-y-4'>
          {isCanvasMode ? (
            <div className='rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/50'>
              <div className='flex items-center justify-between'>
                <div className='flex items-center gap-2'>
                  <span className='text-sm font-medium text-gray-700 dark:text-gray-300'>
                    {t('HD Quality')}
                  </span>
                  <SimpleTooltip
                    title={t(
                      'Higher quality output with enhanced details and resolution',
                    )}
                    side='top'
                  >
                    <QuestionMarkCircleIcon className='h-4 w-4 text-gray-400 cursor-help' />
                  </SimpleTooltip>
                </div>
                <Toggle
                  initialValue={useHD}
                  onChange={(checked) => setUseHD(checked)}
                  disabled={isGenerating}
                  isSmall={true}
                />
              </div>

              <input type='hidden' name='useHD' value={useHD ? '1' : '0'} />
            </div>
          ) : (
            <>
              <div className='space-y-3'>
                <label className='block text-sm font-medium text-gray-700 dark:text-gray-300'>
                  {t('AI Model')}
                </label>
                <div className='relative'>
                  <select
                    value={selectedModel}
                    onChange={(e) => setSelectedModel(e.target.value)}
                    className='w-full appearance-none rounded-lg border border-gray-200 bg-white px-4 py-2.5 pr-10 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white dark:focus:border-primary-400 dark:focus:ring-primary-400/20'
                    disabled={isGenerating}
                    name='editModel'
                  >
                    {availableModels.map((model) => {
                      const isLocked =
                        isFreePlan && isPremiumEditModel(model.value);
                      return (
                        <option
                          key={model.value}
                          value={model.value}
                          disabled={isLocked}
                        >
                          {model.label}
                          {isLocked
                            ? ` — 👑 ${t('Upgrade to use this model')}`
                            : ''}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
              {(selectedModel === 'FLUX.2 [pro]' ||
                selectedModel === 'FLUX.2 [flex]' ||
                selectedModel === 'Seedream 4.5' ||
                selectedModel === 'Seedream 5.0 Lite' ||
                selectedModel === 'Seedream 4.0' ||
                selectedModel === 'OpenAI GPT Image 1.5') && (
                <div className='rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/50'>
                  <div className='flex items-center justify-between'>
                    <div className='flex items-center gap-2'>
                      <span className='text-sm font-medium text-gray-700 dark:text-gray-300'>
                        {t('HD Quality')}
                      </span>
                      <SimpleTooltip
                        title={t(
                          'Higher quality output with enhanced details and resolution',
                        )}
                        side='top'
                      >
                        <QuestionMarkCircleIcon className='h-4 w-4 text-gray-400 cursor-help' />
                      </SimpleTooltip>
                    </div>
                    <Toggle
                      initialValue={useHD}
                      onChange={(checked) => setUseHD(checked)}
                      disabled={isGenerating}
                      isSmall={true}
                    />
                  </div>

                  <input type='hidden' name='useHD' value={useHD ? '1' : '0'} />
                </div>
              )}
            </>
          )}

          {/* Dimensions and Resolution Row */}
          {!isCanvasMode && dimensionOptions.length > 0 && (
            <div className='flex items-center gap-2'>
              <AspectRatioSelector
                value={dimensions || 'auto'}
                onChange={(value) => onDimensionsChange?.(value)}
                options={dimensionOptions}
                disabled={isGenerating}
              />
              {resolutionOptions.length > 0 && (
                <ResolutionSelector
                  value={resolutionValue || resolutionOptions[0] || ''}
                  onChange={(value) => onResolutionChange?.(value)}
                  options={resolutionOptions}
                  disabled={isGenerating}
                />
              )}
            </div>
          )}

          <Button
            variant='dark'
            className='w-full justify-center'
            type='submit'
            disabled={isSubmitDisabled}
            isLoading={isGenerating}
          >
            <span translate='no'>
              {isGenerating
                ? t('Generating...')
                : `${t('Generate')} • ${
                    isCanvasMode
                      ? useHD
                        ? '4'
                        : '2'
                      : getCreditsForModel(selectedModel)
                  } ${t('credits')}`}
            </span>
          </Button>
        </div>
      </div>
    </form>
  );
});

export default PromptForm;
